import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ProtocolDetailDto, ProtocolFilterDto, ProtocolService, ProtocolTypeItemDto, ProtocolTypeService, UserDetailDto } from '@artemis-software/wr-api';
import { CurrentUserService } from '@services/current-user.service';
import { currentTenantId } from '@/utils/admin-utils';
import { NbDialogService } from '@nebular/theme';
import {
  ProtocolCreateDialogComponent,
  ProtocolCreateResult,
} from '@components/dialog/protocol-create-dialog/protocol-create-dialog.component';

@Component({
  standalone: false,
  selector: 'wr-building-equipment-protocols-tab',
  templateUrl: './building-equipment-protocols-tab.component.html',
  styleUrl: './building-equipment-protocols-tab.component.scss',
})
export class BuildingEquipmentProtocolsTabComponent {

  private readonly router = inject(Router);
  private readonly protocolService = inject(ProtocolService);
  private readonly protocolTypeService = inject(ProtocolTypeService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly fb = inject(FormBuilder);
  private readonly dialogService = inject(NbDialogService);

  readonly buildingEquipmentId = input.required<string>();
  readonly tenantId = currentTenantId();
  readonly canCreate = computed(() => this.currentUserService.hasPrivilege('canCreateProtocols'));

  readonly form: FormGroup;
  readonly protocolTypes$ = this.protocolTypeService.findAll({ tenantId: this.tenantId() });

  readonly loadItems = (filter: ProtocolFilterDto) =>
    this.protocolService.findAllDetailDtos({
      ...filter,
      tenantId: this.tenantId(),
      buildingEquipmentId: this.buildingEquipmentId(),
    });

  readonly getCount = (filter: ProtocolFilterDto) =>
    this.protocolService.getCount({
      ...filter,
      tenantId: this.tenantId(),
      buildingEquipmentId: this.buildingEquipmentId(),
    });

  constructor() {
    this.form = this.fb.group({
      text: [''],
      protocolTypeId: [null],
    });
  }

  navigateToDetail(item: ProtocolDetailDto): void {
    this.router.navigate(['protocols', item.id]);
  }

  getUserProfile(user: UserDetailDto | undefined): string {
    const fallback = 'https://i.imgur.com/ujW6tKI.png';
    return user?.userProfilePicture?.presignedUrl ?? fallback;
  }

  getCreatorName(item: ProtocolDetailDto): string {
    if (item.creator) {
      return `${item.creator.firstName} ${item.creator.lastName}`;
    }
    return item.creatorInfo?.name || 'Anonym';
  }

  readonly exportCreatorName = (item: ProtocolDetailDto): string => this.getCreatorName(item);

  createProtocol(): void {
    this.dialogService.open(ProtocolCreateDialogComponent, {
      context: { preselectedBuildingEquipmentId: this.buildingEquipmentId() },
    }).onClose.subscribe((result: ProtocolCreateResult | null) => {
      if (!result) return;
      const queryParams: Record<string, string> = { protocolTypeId: result.protocolTypeId };
      if (result.buildingId) queryParams['buildingId'] = result.buildingId;
      if (result.buildingEquipmentId) queryParams['buildingEquipmentId'] = result.buildingEquipmentId;
      this.router.navigate(['protocols', 'new'], { queryParams });
    });
  }
}
